import { AxiosResponse } from 'axios';
import SubscriptionCard from 'app/module/TrialPeriod/SubscriptionCard';
import { useQuery } from 'react-query';
import Placeholder from '@guestyci/foundation/Placeholder';
import { Col } from '@guestyci/foundation/Layout';
import { useAuthorizationContext } from 'app/module/Authorization/AuthorizationContainer';
import GenericError from 'app/shareable/GenericError';

import getCompanyInfo from 'API/handlers/getCompanyInfo';
import getPaymentInfo from 'API/handlers/getPaymentInfo';

interface IPeriod {
  id: string;
  listingCount: number;
  price: string;
  sale: string;
  discount: string;
  title: string;
  footerText: string;
}

const selectCountryCode = (response: AxiosResponse) =>
  response.data?.companyInformation?.country || '';

export interface SubscriptionCardView {
  id: string;
  listingCount: number;
  price: string;
  sale: string;
  discount: string;
  title: string;
  footerText: string;
}

const selectSubscriptionStats = (response: AxiosResponse<GfhPaymentInfo>) => {
  return Object.entries(response.data.subscriptionInfo)
    .reduce((acc: IPeriod[], [key, obj]) => {
      const regularPrice = response.data.subscriptionInfo.monthly.perMonth;
      const period: SubscriptionCardView = {
        id: key,
        listingCount: response.data.listingCount,
        price: `${response.data.currency}${obj.perMonth}`,
        sale: obj.discountPercent ? `Save ${obj.discountPercent}` : '',
        discount: obj.discount
          ? `${response.data.currency}${regularPrice}`
          : '',
        title: `${key} Plan`,
        footerText: key === 'annual' ? '*Billed annually' : `*Billed ${key}`,
      };

      return acc.concat(period);
    }, [])
    .reverse();
};

function Offers() {
  const { state } = useAuthorizationContext();

  const companyInfoResp = useQuery({
    queryKey: 'CompanyInformation',
    queryFn: () => getCompanyInfo(state.userId),
    select: selectCountryCode,
  });

  const paymentInfoResp = useQuery({
    queryKey: 'PaymentInfo',
    queryFn: () =>
      getPaymentInfo({
        countryCode: companyInfoResp.data,
        userId: state.userId,
      }),
    enabled: companyInfoResp.isFetched,
    select: selectSubscriptionStats,
  });

  if (companyInfoResp.isFetching || paymentInfoResp.isFetching) {
    return (
      <Col spacing={4} fullWidth className="flex-grow-1">
        <Placeholder className="height-50" />
        <Placeholder className="height-50" />
        <Placeholder className="height-50" />
        <Placeholder className="height-50" />
        <Placeholder className="height-50" />
        <Placeholder className="height-50" />
        <Placeholder className="height-50" />
      </Col>
    );
  }

  if (paymentInfoResp.isError) {
    return (
      <Col fullWidth className="flex-grow-1">
        <GenericError text="Can't load offers" />
      </Col>
    );
  }

  return (
    <>
      {paymentInfoResp.data?.map((subscription) => (
        <SubscriptionCard
          key={subscription.id}
          isPreselected={subscription.id === 'annual'}
          data={subscription}
        />
      ))}
    </>
  );
}

export default Offers;
